import { useState } from "react";
import { useSupabaseAuth } from "@ugg/shared/components/auth/SupabaseAuthProvider";
import { useAuthCaptcha } from "@ugg/shared/hooks/use-auth-captcha";
import { CAPTCHA_ACTIONS } from "@ugg/shared/utils/captcha";
import { authErrorMessage } from "@ugg/shared/utils/auth-captcha";

type EmailLoginStep = "credentials" | "code";

export function useEmailLogin(onSignedIn: () => void | Promise<void>) {
  const { client } = useSupabaseAuth();
  const captcha = useAuthCaptcha();
  const [step, setStep] = useState<EmailLoginStep>("credentials");
  const [pendingEmail, setPendingEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const submitCredentials = async (email: string, password: string) => {
    setError("");
    setIsLoading(true);
    try {
      const captchaToken = await captcha.mint(CAPTCHA_ACTIONS.login, setError);
      if (!captchaToken) {
        return;
      }
      const { error: signInError } = await client.auth.signInWithPassword({ email, password, options: { captchaToken } });
      if (!signInError) {
        await onSignedIn();
        return;
      }
      if (signInError.code !== "email_not_confirmed") {
        setError(authErrorMessage(signInError));
        return;
      }
      const resendToken = await captcha.mint(CAPTCHA_ACTIONS.resend, setError);
      if (!resendToken) {
        return;
      }
      const { error: resendError } = await client.auth.resend({ type: "signup", email, options: { captchaToken: resendToken } });
      if (resendError) {
        setError(authErrorMessage(resendError));
        return;
      }
      setPendingEmail(email);
      setStep("code");
    } finally {
      setIsLoading(false);
    }
  };

  const submitCode = async (code: string) => {
    setError("");
    setIsLoading(true);
    try {
      const { error: verifyError } = await client.auth.verifyOtp({ email: pendingEmail, token: code, type: "email" });
      if (verifyError) {
        setError(verifyError.message);
        return;
      }
      await onSignedIn();
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    setError("");
    const captchaToken = await captcha.mint(CAPTCHA_ACTIONS.resend, setError);
    if (!captchaToken) {
      return;
    }
    const { error: resendError } = await client.auth.resend({ type: "signup", email: pendingEmail, options: { captchaToken } });
    if (resendError) {
      setError(authErrorMessage(resendError));
    }
  };

  const reset = () => {
    captcha.cancel();
    setError("");
    setPendingEmail("");
    setStep("credentials");
  };

  return { step, error, isLoading, submitCredentials, submitCode, resendCode, reset, captchaContainerRef: captcha.containerRef };
}
